import {
    Distance,
    IAlarmStatus,
    TransportWeight,
} from '../components/Applications/Intransit/interfaces'
import { DeserializedTargetAttribute } from '../components/DTStudio/interfaces'
import { IMRTColumn } from '../components/Entity/interfaces'
import { GeoJSONDataSource } from '../components/ViewEntity/TabsView/MapView/DynamicDataSourceMap'
import {
    ACK_REQUIRING_SEVERITIES,
    ALERT_STATUS,
    AttributeFunctionalType,
    AttributeType,
    thresholds,
} from '../constants/constants'
import { IRules } from '../services/apiInterfaces'

export const getRandomDarkColor = (): string => {
    const getHex = (value: number) => value.toString(16).padStart(2, '0')

    // Limit RGB values to the range of 0-100 for dark colors
    const r = Math.floor(Math.random() * 151) // 0-100
    const g = Math.floor(Math.random() * 151) // 0-100
    const b = Math.floor(Math.random() * 151) // 0-100

    return `#${getHex(r)}${getHex(g)}${getHex(b)}`
}

export const getRandomPastelColor = (): string => {
    const getHex = (value: number) => value.toString(16).padStart(2, '0')

    // Generate pastel colors by limiting RGB values to the range of 200-255
    const r = Math.floor(Math.random() * 56) + 200 // 200-255
    const g = Math.floor(Math.random() * 56) + 200 // 200-255
    const b = Math.floor(Math.random() * 56) + 200 // 200-255

    return `#${getHex(r)}${getHex(g)}${getHex(b)}`
}

export const getIndexedColor = (index: number, opacity = 0.35): string => {
    // golden angle in degrees, spreads out hues uniformly
    const HUE_STEP = 137.508
    const hue = (index * HUE_STEP) % 360

    // value in percentages
    const saturation = 20
    const lightness = 60

    // toFixed(1) just makes the string prettier
    return `hsla(${hue.toFixed(1)}, ${saturation}%, ${lightness}%, ${opacity})`
}

export const getDataSourceKey = (source: GeoJSONDataSource): string =>
    `${source.attributeName}-${source.entity}`

export const isObject = (value: unknown): boolean =>
    typeof value === 'object' && value !== null

export const isGPS = (item: IMRTColumn | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.GPS
}

export const isGeoFence = (item: IMRTColumn | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.GEO_FENCING
}

export const isURL = (item: IMRTColumn | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.URL
}

export const isPDFBase64 = (item: IMRTColumn | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.PDFBASE64
}

export const isEntity = (item: IMRTColumn | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.ENTITY
}

export const isEntityJson = (item: IMRTColumn | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.Entity_JSON
}

export const isJson = (item: IMRTColumn | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.JSON
}

export const isFile = (item: IMRTColumn | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.FILE
}

export const isWeight = (item: IMRTColumn | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.WEIGHT
}

export const isUser = (item: IMRTColumn | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.USER
}

export const isUserGroup = (item: IMRTColumn | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.USER_GROUP
}

export const isDate = (item: IMRTColumn | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.DATE
}

export const isDateTime = (item: IMRTColumn | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.DATE_TIME
}

export const isCreationDateTime = (item: IMRTColumn | undefined): boolean => {
    return (
        item?.dataType?.toLocaleLowerCase() === AttributeType.CREATION_DATETIME
    )
}

export const isTemperature = (item: IMRTColumn | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.TEMPERATURE
}

export const isHumidity = (item: IMRTColumn | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.HUMIDITY
}

export const isDistance = (item: IMRTColumn | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.DISTANCE
}

export const isBoolean = (item: IMRTColumn | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.BOOLEAN
}

export const isRuleSet = (item: IMRTColumn | undefined): boolean => {
    return item?.dataType?.toLocaleLowerCase() === AttributeType.RULESET
}

export const isStatic = (item: IMRTColumn | undefined): boolean => {
    return item?.attributeSourceType === AttributeFunctionalType.Static
}

export const isAttributeTypeStatic = (
    item: IMRTColumn | undefined,
): boolean => {
    return item?.attributeType === AttributeFunctionalType.Static
}

export const isDevOrQAEnv = (): boolean => {
    const environment = import.meta.env.VITE_API_ENTITY_MGMT_URL ?? ''
    return ['dev', 'qa'].some((env) => environment.includes(env))
}

export const shouldThresholdLineBeDisplayed = (type: string): boolean => {
    return thresholds[type as keyof IRules]?.displayOnGraph
}

export const getDataTypeFromTargetAttribute = (
    targetAttribute: string,
): string => {
    //targetAttribute = {"value":"device.temperature_1","isRelation":true,"dataType":"Temperature","displayName":"Temperature 1","units":"°F"}
    try {
        const parsed = JSON.parse(targetAttribute)
        return parsed.dataType || ''
    } catch {
        return ''
    }
}

export const getDisplayNameFromTargetAttribute = (
    targetAttribute: string,
): string => {
    //targetAttribute = {"value":"device.temperature_1","isRelation":true,"dataType":"Temperature","displayName":"Temperature 1","units":"°F"}
    try {
        const parsed: DeserializedTargetAttribute = JSON.parse(targetAttribute)
        return parsed.displayName || ''
    } catch {
        return ''
    }
}

/**
 * UBQ-5487: has every ack-requiring alert on this shipment been acted on?
 *
 * Acknowledged **or** Ignored counts — both are a deliberate decision by a user, which is what the
 * checkmark reports, per UBQ-5337's AC "every ack-requiring alert is Acknowledged or Ignored". Only
 * Unread blocks it, so setting an alert back to Unread drops the checkmark (UBQ-5488).
 *
 * The status the backend sends is each alert firing's *latest* status, so this needs no notion of
 * history: an acknowledgment that was later revoked arrives here as Unread.
 *
 * Returns false when there is nothing to acknowledge: a shipment with no critical alert has not
 * been "attended to", it simply never needed attention, and must not show the checkmark.
 */
export const isAlarmFullyAcknowledged = (
    alarmStatus: IAlarmStatus | undefined,
): boolean => {
    if (!alarmStatus) {
        return false
    }

    const criticalAlarms = ACK_REQUIRING_SEVERITIES.flatMap(
        (key) => alarmStatus[key] ?? [],
    )

    if (criticalAlarms.length === 0) return false

    return criticalAlarms.every(
        (alarm) =>
            alarm.status === ALERT_STATUS.ACKNOWLEDGED ||
            alarm.status === ALERT_STATUS.IGNORED,
    )
}

export const isCriticalAlarm = (
    alarmStatus: IAlarmStatus | undefined,
): boolean => {
    if (!alarmStatus) {
        return false
    }

    const criticalAlarms = ACK_REQUIRING_SEVERITIES.flatMap(
        (key) => alarmStatus[key] ?? [],
    )
    if (criticalAlarms.length === 0) return false

    return true
}

export const formatDistance = (
    distance: number | Distance | undefined | null,
): string => {
    if (distance === undefined || distance === null) {
        return ''
    }
    if (typeof distance === 'object' && !Number.isNaN(Number(distance.value))) {
        return `${Number(distance.value).toFixed(1)} ${distance.units}`
    }
    if (!Number.isNaN(Number(distance))) {
        return Number(distance).toFixed(1)
    }
    return ''
}

export const formatTransportWeight = (
    transportWeight: number | TransportWeight,
): string => {
    if (
        typeof transportWeight === 'object' &&
        !Number.isNaN(Number(transportWeight.value))
    ) {
        return `${Number(transportWeight.value).toFixed(1)} ${transportWeight.units}`
    }
    if (!Number.isNaN(Number(transportWeight))) {
        return Number(transportWeight).toFixed(1)
    }
    return ''
}

export const isPlainObject = (
    value: unknown,
): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}
